import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { MenuController, ModalController, NavController } from "@ionic/angular";
import { SharedDataService } from "../../services/shared-data.service";
import { ObservablesService } from "../../services/observables.service";
import { EnumService } from "../../services/enum.service";
import { HomeExitDmPage } from "../../modals/home-exit-dm/home-exit-dm.page";
import { AccountService } from "../../services/account.service";
import { UtilService } from "src/app/services/util.service";
import { PluginListenerHandle } from "@capacitor/core";
import { Network, NetworkStatus } from '@capacitor/network';
import { OfflineManagerService } from "src/app/services/offline-manager.service";
import { ApiService } from "src/app/services/api.service";
import { BackgroundSyncService, SyncStatus } from "../../services/background-sync.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-dashboard-header-dm",
  templateUrl: "./dashboard-header-dm.component.html",
  styleUrls: ["./dashboard-header-dm.component.scss"],
})
export class DashboardHeaderDmComponent implements OnInit, OnDestroy {
  @Input() title = "";
  networkChangeListner: PluginListenerHandle;
  syncStatusSubscription: Subscription;

  isSycronizationNeeded = false;
  isSyncButtonDisabled = false;
  isSyncing = false; // Track sync status for header loader
  private hideLoaderTimer: any = null;

  isWebApp: Boolean = UtilService.isWebApp();
  headerColor;
  menuColor;
  subMenuColor;
  logo;

  constructor(
    public navController: NavController,
    public modalController: ModalController,
    private menu: MenuController,
    public sharedDataService: SharedDataService,
    public accountService: AccountService,
    public offlineManagerService: OfflineManagerService,
    public observablesService: ObservablesService,
    private cdr: ChangeDetectorRef,
    private backgroundSyncService: BackgroundSyncService
  ) {
    this.checkForSyncNeeded();

    this.observablesService
      .getObservable(EnumService.ObserverKeys.OFFLINE_DATA_SYNC_NEEDED)
      .subscribe((status: boolean) => {
        this.checkForSyncNeeded();
      });
  }

  async ngOnInit() {
    await this.checkForNetwork();
    this.sharedDataService.headerColor$.subscribe(color => {
      this.headerColor = color;
    });
    this.sharedDataService.menuColor$.subscribe(color => {
      this.menuColor = color;
    });
    this.sharedDataService.subMenuColor$.subscribe(color => {
      this.subMenuColor = color;
    });
    this.sharedDataService.logoPathSubject$.subscribe(logo => {
      this.logo = logo;
    });
    this.networkChangeListner = await Network.addListener(
      'networkStatusChange',
      (status: NetworkStatus) => {
        this.checkForNetwork();
        this.cdr.detectChanges();
      }
    );

    // Subscribe to sync status to show/hide header loader
    this.syncStatusSubscription = this.backgroundSyncService.getSyncStatus().subscribe((status: SyncStatus) => {
      // Show header loader for any sync in progress (initial sync, background sync, signoff sync, etc.)
      // checking_database: pending check-ins / early startBackgroundSync phase before full/incremental sync
      const shouldBeSyncing = status.isSyncing && (
        status.status === 'checking_database' ||
        status.status === 'background_sync_started' ||
        status.status === 'full_sync_started' ||
        status.status === 'downloading_data' ||
        status.status === 'inserting_data' ||
        status.status === 'preparing_database' ||
        status.status === 'preparing_data' ||
        status.status === 'signoff_sync_started'
      );

      if (shouldBeSyncing) {
        if (this.hideLoaderTimer) {
          clearTimeout(this.hideLoaderTimer);
          this.hideLoaderTimer = null;
        }
        this.isSyncing = true;
        this.cdr.detectChanges();
      } else if (this.isSyncing && !this.hideLoaderTimer) {
        this.hideLoaderTimer = setTimeout(() => {
          this.isSyncing = false;
          this.hideLoaderTimer = null;
          this.cdr.detectChanges();
        }, 2000);
      }
    });
  }

  checkForSyncNeeded = () => {
    this.offlineManagerService
      .shouldOnlyUseInOfflineMode()
      .then((status: boolean) => {
        this.isSycronizationNeeded = status;
      });
  };

  ngOnDestroy(): void {
    if (this.networkChangeListner) {
      this.networkChangeListner.remove();
    }
    if (this.syncStatusSubscription) {
      this.syncStatusSubscription.unsubscribe();
    }
    if (this.hideLoaderTimer) {
      clearTimeout(this.hideLoaderTimer);
    }
  }

  checkForNetwork = async () => {
    const ntstatus = await Network.getStatus();
    this.isSyncButtonDisabled = !ntstatus.connected;
    this.cdr.detectChanges();
  };

  async syncClick() {
    this.navController.navigateForward("device-sync-dm");
  }

  infoClick() {
    this.menu.open("infoMenu");
  }

  async logoutClick() {
    const modal = await this.modalController.create({
      component: HomeExitDmPage,
      cssClass: "home-exit-dm",
      componentProps: {
        title: "Exit",
        description:
          "You are about to exit this location that this device is dedicated for at the moment.\n" +
          "Are you sure you want to exit?",
        okBtnText: "Yes",
        cancelBtnText: "Cancel",
      },
    });
    await modal.present();

    modal.onWillDismiss().then(({ data }) => {
      if (data) {
        this.sharedDataService.resetAllSharedVariable();
        localStorage.removeItem(
          EnumService.LocalStorageKeys.DEDICATED_MODE_LOCATION_USE
        );
        this.navController.navigateRoot("choose-location");
      }
    });
  }
}
